import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge } from "@phosphor-icons/react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { advisorHeaders, CATEGORY_ORDER, scoreTone } from "../lib/clarity";
import AdminClarityDetail from "../components/clarity/AdminClarityDetail";
import { WeightsEditor, Funnel } from "../components/clarity/AdminClarityTools";

const TABS = [["leads", "Assessments & Leads"], ["funnel", "Funnel"], ["weights", "Scoring Weights"]];

export default function AdminClarity() {
  const [tab, setTab] = useState("leads");
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [l, s] = await Promise.all([api.get("/admin/clarity", advisorHeaders()), api.get("/admin/clarity/stats", advisorHeaders())]);
      setItems(l.data.items); setCats(l.data.lead_categories); setStats(s.data);
    } catch { toast.error("Could not load Clarity Scores. Please sign in with an advisor account."); }
  };
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    try { const r = await api.get(`/admin/clarity/${id}`, advisorHeaders()); setDetail(r.data); } catch { toast.error("Could not open record."); }
  };

  const needle = q.toLowerCase().trim();
  const shown = items.filter((i) => (!filter || i.lead_category === filter) &&
    (!needle || [i.contact?.first_name, i.contact?.email, i.contact?.phone, i.pathway, i.top_goal].some((x) => (x || "").toLowerCase().includes(needle))));

  return (
    <DashboardLayout>
      <div data-testid="admin-clarity-page" className="p-6 lg:p-10">
        <div className="flex items-start justify-between gap-6 flex-wrap mb-8 pl-12 lg:pl-0">
          <div>
            <p className="eyebrow flex items-center gap-2"><Gauge size={16} weight="fill" /> Financial Clarity Score</p>
            <h1 className="font-display text-4xl mt-2">Every assessment, every answer.</h1>
            <p className="text-sm text-exodus-muted mt-2 max-w-2xl">Lead categories reflect stated preferences and consent — not wealth. Open any record to see all answers, correct inputs, and add review notes.</p>
          </div>
          {stats && (
            <div className="flex gap-8 text-right">
              <div><p className="eyebrow text-[10px] text-exodus-muted">Assessments</p><p data-testid="admin-clarity-total" className="font-display text-4xl gold-text">{stats.total}</p></div>
              <div><p className="eyebrow text-[10px] text-exodus-muted">Avg score</p><p className="font-display text-4xl">{stats.average_score ?? "—"}</p></div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-exodus-border">
          {TABS.map(([k, l]) => (
            <button key={k} data-testid={`admin-clarity-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-3 text-sm -mb-px border-b-2 transition-colors ${tab === k ? "border-exodus-gold text-exodus-gold" : "border-transparent text-exodus-muted hover:text-exodus-text"}`}>{l}</button>
          ))}
        </div>

        {tab === "leads" && (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              <button data-testid="admin-clarity-filter-all" onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 border ${!filter ? "border-exodus-gold text-exodus-gold" : "border-exodus-border text-exodus-muted"}`}>All</button>
              {cats.map((c) => (
                <button key={c} data-testid={`admin-clarity-filter-${c.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setFilter(c)}
                  className={`text-xs px-3 py-1.5 border ${filter === c ? "border-exodus-gold text-exodus-gold" : "border-exodus-border text-exodus-muted"}`}>
                  {c} <span className="ml-1 text-exodus-text">{stats?.by_lead_category?.[c] ?? 0}</span>
                </button>
              ))}
            </div>
            <input data-testid="admin-clarity-search" placeholder="Search name, email, phone, pathway…" value={q} onChange={(e) => setQ(e.target.value)}
              className="w-full bg-exodus-elevated border border-exodus-border px-4 py-3 text-sm mb-6 focus:outline-none focus:border-exodus-gold" />
            <div className="border border-exodus-border overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-exodus-elevated"><tr className="text-left eyebrow text-[10px] text-exodus-muted">
                  <th className="p-3">Date</th><th className="p-3">Visitor</th><th className="p-3">Score</th>
                  {CATEGORY_ORDER.map((k) => <th key={k} className="p-3">{k}</th>)}
                  <th className="p-3">Lead category</th><th className="p-3">Pathway</th><th className="p-3">Signals</th>
                </tr></thead>
                <tbody>
                  {shown.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-exodus-muted">No assessments yet.</td></tr>}
                  {shown.map((i) => (
                    <tr key={i.id} data-testid={`admin-clarity-row-${i.id}`} onClick={() => open(i.id)} className="border-t border-exodus-border hover:bg-exodus-elevated/50 cursor-pointer transition-colors">
                      <td className="p-3 text-xs text-exodus-muted whitespace-nowrap">{new Date(i.created_at).toLocaleDateString()}</td>
                      <td className="p-3"><p>{i.contact?.first_name || "Anonymous"}</p><p className="text-xs text-exodus-muted">{i.contact?.email || `${i.answer_count} answers`}</p></td>
                      <td className={`p-3 font-display text-2xl ${scoreTone(i.overall)}`}>{i.overall ?? "—"}{i.provisional && <span className="text-[9px] text-orange-300 align-top ml-1">P</span>}</td>
                      {CATEGORY_ORDER.map((k) => <td key={k} className={`p-3 ${scoreTone(i.categories[k])}`}>{i.categories[k] ?? "—"}</td>)}
                      <td className="p-3 text-xs"><span className="border border-exodus-gold/40 px-2 py-1 text-exodus-gold whitespace-nowrap">{i.lead_category}</span></td>
                      <td className="p-3 text-xs text-exodus-muted">{i.pathway}</td>
                      <td className="p-3 text-[10px] text-exodus-muted">{i.tier === "deep" && "DEEP "}{i.booking_clicked && "BOOK "}{i.callback_requested && "CALLBACK"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {tab === "funnel" && stats && <Funnel stats={stats} />}
        {tab === "weights" && <WeightsEditor />}
      </div>
      {detail && <AdminClarityDetail key={detail.id} d={detail} leadCategories={cats} onClose={() => setDetail(null)}
        onChanged={(d) => { setDetail(d); load(); }} />}
    </DashboardLayout>
  );
}
